import { COLORS } from "../../constants/colors";
import {
  HouseIcon,
  CondoIcon,
  TownhomeIcon,
  MultiUnitIcon,
  LandIcon,
  MobileHomeIcon,
  CommercialIcon,
  OtherPropertyIcon,
} from "src/components/icons";

export const PROPERTY_TYPES = {
  house: "S",
  condo: "C",
  townhome: "T",
  multiUnit: "M",
  land: "L",
  mobileHome: "B",
  commercial: "O",
  otherTypes: "I"
};

// Valid property type codes (single letters)
const VALID_PROPERTY_TYPE_CODES = new Set(Object.values(PROPERTY_TYPES));

/**
 * Parse property type string from API into an array.
 * Handles both comma-delimited ("S,C,T") and concatenated ("SCT") formats.
 */
export const parsePropertyTypes = (propertyType: string | undefined | null): string[] => {
  if (!propertyType) return [];

  // If it contains commas, split on commas
  if (propertyType.includes(',')) {
    return propertyType.split(',').filter(Boolean);
  }

  // Otherwise, split each character (handles "SCT" -> ["S", "C", "T"])
  // Only include valid property type codes
  return propertyType.split('').filter(char => VALID_PROPERTY_TYPE_CODES.has(char));
};

export const DEFAULT_PROPERTY_TYPES_FILTERS = [
  PROPERTY_TYPES.house,
  PROPERTY_TYPES.condo,
  PROPERTY_TYPES.townhome,
];

// New array with value and label keys, similar to allPropertyTypes in PropertyTypeFilter
export const PROPERTY_TYPE_OPTIONS = [
  { value: PROPERTY_TYPES.house, label: "House", icon: <HouseIcon /> },
  { value: PROPERTY_TYPES.condo, label: "Condo", icon: <CondoIcon /> },
  { value: PROPERTY_TYPES.townhome, label: "Townhome", icon: <TownhomeIcon /> },
  { value: PROPERTY_TYPES.multiUnit, label: "Multi-Unit", icon: <MultiUnitIcon /> },
  { value: PROPERTY_TYPES.land, label: "Land", icon: <LandIcon /> },
  { value: PROPERTY_TYPES.mobileHome, label: "Mobile Home", icon: <MobileHomeIcon /> },
  { value: PROPERTY_TYPES.commercial, label: "Commercial", icon: <CommercialIcon /> },
  { value: PROPERTY_TYPES.otherTypes, label: "Other", icon: <OtherPropertyIcon /> }
];

export const OWNER_OCCUPANCY_FILTER = {
  ALL: "all",
  OWNER: "owner",
  NON_OWNER: "non-owner"
} as const;

export type OwnerOccupancyFilter = typeof OWNER_OCCUPANCY_FILTER[keyof typeof OWNER_OCCUPANCY_FILTER];

export const OWNER_OCCUPANCY_OPTIONS = [
  { value: OWNER_OCCUPANCY_FILTER.ALL, label: "All Properties" },
  { value: OWNER_OCCUPANCY_FILTER.OWNER, label: "Owner Occupied Only" },
  { value: OWNER_OCCUPANCY_FILTER.NON_OWNER, label: "Non-Owner Occupied Only" }
];

export const COMMON_SHAPE_OPTIONS = {
  fillColor: COLORS.GRAY_600,
  fillOpacity: 0.2,
  strokeColor: COLORS.GRAY_600,
  strokeOpacity: 1,
  strokePosition: window.google?.maps?.StrokePosition.CENTER,
  strokeWeight: 1,
};

export enum SavedSearchLookupStatusCode {
  Complete = "C",
  Queued = "Q",
  InProgress = "I",
  Failed = "F"
}

export interface IExportColumn {
  key: string;
  header: string;
}

export interface IExportTemplate {
  id: string;
  name: string;
  hiddenColumns: string[];
  order: string[];
}

export const EXPORT_TEMPLATES: IExportTemplate[] = [
  {
    id: "basic",
    name: "Basic",
    hiddenColumns: [
      "contact1FullName",
      "houseNumber",
      "streetPredirection",
      "streetName",
      "streetPostdirection",
      "streetSuffix",
      "legalUnit",
      "postalCodePlus4",
      "propertyType",
      "companyOwned",
      "bedrooms",
      "bathrooms",
      "partialBathrooms",
      "squareFootage",
      "acreage",
      "yearBuilt",
      "latitude",
      "longitude",
      "accuracy",
      "partyOwner1FirstName",
      "partyOwner1LastName",
      "partyOwner1FullName",
      "partyOwner1MiddleName",
      "partyOwner1Order",
      "partyOwner2FirstName",
      "partyOwner2MiddleName",
      "partyOwner2LastName",
      "partyOwner2FullName",
      "partyOwner2Order",
      "partyOwner3FirstName",
      "partyOwner3MiddleName",
      "partyOwner3LastName",
      "partyOwner3FullName",
      "partyOwner3Order",
      "partyOwner4FirstName",
      "partyOwner4MiddleName",
      "partyOwner4LastName",
      "partyOwner4FullName",
      "partyOwner4Order",
      "deedOwner1FirstName",
      "deedOwner1MiddleName",
      "deedOwner1LastName",
      "deedOwner1FullName",
      "deedOwner1Order",
      "deedOwner2FirstName",
      "deedOwner2MiddleName",
      "deedOwner2LastName",
      "deedOwner2FullName",
      "deedOwner2Order",
      "deedOwner3FirstName",
      "deedOwner3MiddleName",
      "deedOwner3LastName",
      "deedOwner3FullName",
      "deedOwner3Order",
      "deedOwner4FirstName",
      "deedOwner4MiddleName",
      "deedOwner4LastName",
      "deedOwner4FullName",
      "deedOwner4Order",
      "owner1Owner2Relationship",
      "owner3Owner4Relationship",
      "ownerOccupied",
      "mailingAddress",
      "mailingHouseNumber",
      "mailingStreetName",
      "mailingStreetPredirection",
      "mailingStreetPostdirection",
      "mailingStreetSuffix",
      "mailingUnit",
      "mailingLegalUnit",
      "mailingCity",
      "mailingState",
      "mailingPostalCode",
      "mailingPostalCodePlus4",
      "mailingLatitude",
      "mailingLongitude",
      "mailingAccuracy",
      "lastSaleDate",
      "lastSalePrice",
      "contact1MiddleName",
      "contact1PhoneType",
      "contact1Age",
      "contact1Gender",
      "contact1Income",
      "contact1MaritalStatus",
      "contact1NumberOfChildren",
      "contact1Education",
      "contact1HomeValueRangeStart",
      "contact1HomeValueRangeEnd",
      "contact1NetWorth",
      "contact1Occupation",
      "contact1BusinessOwner",
      "contact2FirstName",
      "contact2MiddleName",
      "contact2LastName",
      "contact2FullName",
      "contact2PhoneType",
      "contact2Email",
      "contact2Age",
      "contact2Gender",
      "contact2Income",
      "contact2MaritalStatus",
      "contact2NumberOfChildren",
      "contact2Education",
      "contact2HomeValueRangeStart",
      "contact2HomeValueRangeEnd",
      "contact2NetWorth",
      "contact2Occupation",
      "contact2BusinessOwner",
      "contact3FirstName",
      "contact3MiddleName",
      "contact3LastName",
      "contact3FullName",
      "contact3PhoneType",
      "contact3Email",
      "contact3Age",
      "contact3Gender",
      "contact3Income",
      "contact3MaritalStatus",
      "contact3NumberOfChildren",
      "contact3Education",
      "contact3HomeValueRangeStart",
      "contact3HomeValueRangeEnd",
      "contact3NetWorth",
      "contact3Occupation",
      "contact3BusinessOwner",
      "contactHouseNumber",
      "contactStreetName",
      "contactStreetPredirection",
      "contactStreetPostdirection",
      "contactStreetSuffix",
      "contactUnit",
      "contactLegalUnit",
      "contactCity",
      "contactState",
      "contactPostalCode",
      "contactPostalCodePlus4",
      "contactLatitude",
      "contactLongitude",
      "contactAccuracy",
      "leadType",
      "hidden",
      "createTimestamp",
      "favorite",
    ],
    order: [
      "contact1FirstName",
      "contact1LastName",
      "propertyAddress",
      "unit",
      "city",
      "state",
      "postalCode",
      "contact1PhoneNumber",
      "contact1DoNotCall",
      "contact2PhoneNumber",
      "contact2DoNotCall",
      "contact3PhoneNumber",
      "contact3DoNotCall",
      "contact1Email",
      "tags",
      "notes",
    ],
  },
  {
    id: "brivity",
    name: "Brivity",
    hiddenColumns: [
      "contact1FullName",
      "houseNumber",
      "streetPredirection",
      "streetName",
      "streetPostdirection",
      "streetSuffix",
      "legalUnit",
      "postalCodePlus4",
      "propertyType",
      "companyOwned",
      "bedrooms",
      "bathrooms",
      "partialBathrooms",
      "squareFootage",
      "acreage",
      "yearBuilt",
      "latitude",
      "longitude",
      "accuracy",
      "partyOwner1FullName",
      "partyOwner1FirstName",
      "partyOwner1LastName",
      "partyOwner1MiddleName",
      "partyOwner1Order",
      "partyOwner2FirstName",
      "partyOwner2MiddleName",
      "partyOwner2LastName",
      "partyOwner2FullName",
      "partyOwner2Order",
      "partyOwner3FirstName",
      "partyOwner3MiddleName",
      "partyOwner3LastName",
      "partyOwner3FullName",
      "partyOwner3Order",
      "partyOwner4FirstName",
      "partyOwner4MiddleName",
      "partyOwner4LastName",
      "partyOwner4FullName",
      "partyOwner4Order",
      "deedOwner1FirstName",
      "deedOwner1MiddleName",
      "deedOwner1LastName",
      "deedOwner1FullName",
      "deedOwner1Order",
      "deedOwner2FirstName",
      "deedOwner2MiddleName",
      "deedOwner2LastName",
      "deedOwner2FullName",
      "deedOwner2Order",
      "deedOwner3FirstName",
      "deedOwner3MiddleName",
      "deedOwner3LastName",
      "deedOwner3FullName",
      "deedOwner3Order",
      "deedOwner4FirstName",
      "deedOwner4MiddleName",
      "deedOwner4LastName",
      "deedOwner4FullName",
      "deedOwner4Order",
      "owner1Owner2Relationship",
      "owner3Owner4Relationship",
      "ownerOccupied",
      "mailingAddress",
      "mailingHouseNumber",
      "mailingStreetName",
      "mailingStreetPredirection",
      "mailingStreetPostdirection",
      "mailingStreetSuffix",
      "mailingUnit",
      "mailingLegalUnit",
      "mailingCity",
      "mailingState",
      "mailingPostalCode",
      "mailingPostalCodePlus4",
      "mailingLatitude",
      "mailingLongitude",
      "mailingAccuracy",
      "lastSaleDate",
      "lastSalePrice",
      "contact1MiddleName",
      "contact1PhoneType",
      "contact1Age",
      "contact1Gender",
      "contact1Income",
      "contact1MaritalStatus",
      "contact1NumberOfChildren",
      "contact1Education",
      "contact1HomeValueRangeStart",
      "contact1HomeValueRangeEnd",
      "contact1NetWorth",
      "contact1Occupation",
      "contact1BusinessOwner",
      "contact2FirstName",
      "contact2MiddleName",
      "contact2LastName",
      "contact2FullName",
      "contact2PhoneNumber",
      "contact2PhoneType",
      "contact2DoNotCall",
      "contact2Email",
      "contact2Age",
      "contact2Gender",
      "contact2Income",
      "contact2MaritalStatus",
      "contact2NumberOfChildren",
      "contact2Education",
      "contact2HomeValueRangeStart",
      "contact2HomeValueRangeEnd",
      "contact2NetWorth",
      "contact2Occupation",
      "contact2BusinessOwner",
      "contact3FirstName",
      "contact3MiddleName",
      "contact3LastName",
      "contact3FullName",
      "contact3PhoneNumber",
      "contact3PhoneType",
      "contact3DoNotCall",
      "contact3Email",
      "contact3Age",
      "contact3Gender",
      "contact3Income",
      "contact3MaritalStatus",
      "contact3NumberOfChildren",
      "contact3Education",
      "contact3HomeValueRangeStart",
      "contact3HomeValueRangeEnd",
      "contact3NetWorth",
      "contact3Occupation",
      "contact3BusinessOwner",
      "contactHouseNumber",
      "contactStreetName",
      "contactStreetPredirection",
      "contactStreetPostdirection",
      "contactStreetSuffix",
      "contactUnit",
      "contactLegalUnit",
      "contactCity",
      "contactState",
      "contactPostalCode",
      "contactPostalCodePlus4",
      "contactLatitude",
      "contactLongitude",
      "contactAccuracy",
      "leadType",
      "hidden",
      "createTimestamp",
      "favorite",
    ],
    order: [
      "contact1FirstName",
      "contact1LastName",
      "propertyAddress",
      "unit",
      "city",
      "state",
      "postalCode",
      "contact1PhoneNumber",
      "contact1DoNotCall",
      "contact1Email",
      "tags",
      "notes",
    ],
  },
  {
    id: "everything",
    name: "Everything",
    hiddenColumns: [],
    order: [],
  },
];

export const ALL_EXPORT_COLUMNS: IExportColumn[] = [
  // Property Info
  { key: "propertyAddress", header: "Property Address" },
  { key: "houseNumber", header: "House Number" },
  { key: "streetPredirection", header: "Street Predirection" },
  { key: "streetName", header: "Street Name" },
  { key: "streetPostdirection", header: "Street Postdirection" },
  { key: "streetSuffix", header: "Street Suffix" },
  { key: "unit", header: "Unit" },
  { key: "legalUnit", header: "Legal Unit" },
  { key: "city", header: "City" },
  { key: "state", header: "State" },
  { key: "postalCode", header: "Postal Code" },
  { key: "postalCodePlus4", header: "Postal Code +4" },

  // Property Details
  { key: "propertyType", header: "Property Type" },
  { key: "companyOwned", header: "Company Owned" },
  { key: "bedrooms", header: "Bedrooms" },
  { key: "bathrooms", header: "Bathrooms" },
  { key: "partialBathrooms", header: "Partial Bathrooms" },
  { key: "squareFootage", header: "Square Footage" },
  { key: "acreage", header: "Acreage" },
  { key: "yearBuilt", header: "Year Built" },

  // Geolocation
  { key: "latitude", header: "Latitude" },
  { key: "longitude", header: "Longitude" },
  { key: "accuracy", header: "Accuracy" },

  // Party Owners (1-4)
  { key: "partyOwner1FirstName", header: "Party Owner1 First Name" },
  { key: "partyOwner1MiddleName", header: "Party Owner1 Middle Name" },
  { key: "partyOwner1LastName", header: "Party Owner1 Last Name" },
  { key: "partyOwner1FullName", header: "Party Owner1 Full Name" },
  { key: "partyOwner1Order", header: "Party Owner1 Order" },
  { key: "partyOwner2FirstName", header: "Party Owner2 First Name" },
  { key: "partyOwner2MiddleName", header: "Party Owner2 Middle Name" },
  { key: "partyOwner2LastName", header: "Party Owner2 Last Name" },
  { key: "partyOwner2FullName", header: "Party Owner2 Full Name" },
  { key: "partyOwner2Order", header: "Party Owner2 Order" },
  { key: "partyOwner3FirstName", header: "Party Owner3 First Name" },
  { key: "partyOwner3MiddleName", header: "Party Owner3 Middle Name" },
  { key: "partyOwner3LastName", header: "Party Owner3 Last Name" },
  { key: "partyOwner3FullName", header: "Party Owner3 Full Name" },
  { key: "partyOwner3Order", header: "Party Owner3 Order" },
  { key: "partyOwner4FirstName", header: "Party Owner4 First Name" },
  { key: "partyOwner4MiddleName", header: "Party Owner4 Middle Name" },
  { key: "partyOwner4LastName", header: "Party Owner4 Last Name" },
  { key: "partyOwner4FullName", header: "Party Owner4 Full Name" },
  { key: "partyOwner4Order", header: "Party Owner4 Order" },

  // Deed Owners (1-4)
  { key: "deedOwner1FirstName", header: "Deed Owner1 First Name" },
  { key: "deedOwner1MiddleName", header: "Deed Owner1 Middle Name" },
  { key: "deedOwner1LastName", header: "Deed Owner1 Last Name" },
  { key: "deedOwner1FullName", header: "Deed Owner1 Full Name" },
  { key: "deedOwner1Order", header: "Deed Owner1 Order" },
  { key: "deedOwner2FirstName", header: "Deed Owner2 First Name" },
  { key: "deedOwner2MiddleName", header: "Deed Owner2 Middle Name" },
  { key: "deedOwner2LastName", header: "Deed Owner2 Last Name" },
  { key: "deedOwner2FullName", header: "Deed Owner2 Full Name" },
  { key: "deedOwner2Order", header: "Deed Owner2 Order" },
  { key: "deedOwner3FirstName", header: "Deed Owner3 First Name" },
  { key: "deedOwner3MiddleName", header: "Deed Owner3 Middle Name" },
  { key: "deedOwner3LastName", header: "Deed Owner3 Last Name" },
  { key: "deedOwner3FullName", header: "Deed Owner3 Full Name" },
  { key: "deedOwner3Order", header: "Deed Owner3 Order" },
  { key: "deedOwner4FirstName", header: "Deed Owner4 First Name" },
  { key: "deedOwner4MiddleName", header: "Deed Owner4 Middle Name" },
  { key: "deedOwner4LastName", header: "Deed Owner4 Last Name" },
  { key: "deedOwner4FullName", header: "Deed Owner4 Full Name" },
  { key: "deedOwner4Order", header: "Deed Owner4 Order" },

  // Owner Relationships
  { key: "owner1Owner2Relationship", header: "Owner 1 - Owner 2 Relationship" },
  { key: "owner3Owner4Relationship", header: "Owner 3 - Owner 4 Relationship" },
  { key: "ownerOccupied", header: "Owner Occupied" },

  // Mailing Address
  { key: "mailingAddress", header: "Mailing Address" },
  { key: "mailingHouseNumber", header: "Mailing House Number" },
  { key: "mailingStreetName", header: "Mailing Street Name" },
  { key: "mailingStreetPredirection", header: "Mailing Street Predirection" },
  { key: "mailingStreetPostdirection", header: "Mailing Street Postdirection" },
  { key: "mailingStreetSuffix", header: "Mailing Street Suffix" },
  { key: "mailingUnit", header: "Mailing Unit" },
  { key: "mailingLegalUnit", header: "Mailing Legal Unit" },
  { key: "mailingCity", header: "Mailing City" },
  { key: "mailingState", header: "Mailing State" },
  { key: "mailingPostalCode", header: "Mailing Postal Code" },
  { key: "mailingPostalCodePlus4", header: "Mailing Postal Code +4" },
  { key: "mailingLatitude", header: "Mailing Latitude" },
  { key: "mailingLongitude", header: "Mailing Longitude" },
  { key: "mailingAccuracy", header: "Mailing Accuracy" },

  // Tax Info
  { key: "lastSaleDate", header: "Last Sale Date" },
  { key: "lastSalePrice", header: "Last Sale Price" },

  // Contact 1
  { key: "contact1FirstName", header: "Contact1 First Name" },
  { key: "contact1MiddleName", header: "Contact1 Middle Name" },
  { key: "contact1LastName", header: "Contact1 Last Name" },
  { key: "contact1FullName", header: "Contact1 Full Name" },
  { key: "contact1PhoneNumber", header: "Contact1 Phone Number" },
  { key: "contact1PhoneType", header: "Contact1 Phone Type" },
  { key: "contact1DoNotCall", header: "Contact1 Do Not Call" },
  { key: "contact1Email", header: "Contact1 Email Address" },
  { key: "contact1Age", header: "Contact1 Age" },
  { key: "contact1Gender", header: "Contact1 Gender" },
  { key: "contact1Income", header: "Contact1 Income" },
  { key: "contact1MaritalStatus", header: "Contact1 Marital Status" },
  { key: "contact1NumberOfChildren", header: "Contact1 Number Of Children" },
  { key: "contact1Education", header: "Contact1 Education" },
  {
    key: "contact1HomeValueRangeStart",
    header: "Contact1 Home Value Range Start",
  },
  { key: "contact1HomeValueRangeEnd", header: "Contact1 Home Value Range End" },
  { key: "contact1NetWorth", header: "Contact1 NetWorth" },
  { key: "contact1Occupation", header: "Contact1 Occupation" },
  { key: "contact1BusinessOwner", header: "Contact1 Business Owner" },

  // Contact 2
  { key: "contact2FirstName", header: "Contact2 First Name" },
  { key: "contact2MiddleName", header: "Contact2 Middle Name" },
  { key: "contact2LastName", header: "Contact2 Last Name" },
  { key: "contact2FullName", header: "Contact2 Full Name" },
  { key: "contact2PhoneNumber", header: "Contact2 Phone Number" },
  { key: "contact2PhoneType", header: "Contact2 Phone Type" },
  { key: "contact2DoNotCall", header: "Contact2 Do Not Call" },
  { key: "contact2Email", header: "Contact2 Email Address" },
  { key: "contact2Age", header: "Contact2 Age" },
  { key: "contact2Gender", header: "Contact2 Gender" },
  { key: "contact2Income", header: "Contact2 Income" },
  { key: "contact2MaritalStatus", header: "Contact2 Marital Status" },
  { key: "contact2NumberOfChildren", header: "Contact2 Number Of Children" },
  { key: "contact2Education", header: "Contact2 Education" },
  {
    key: "contact2HomeValueRangeStart",
    header: "Contact2 Home Value Range Start",
  },
  { key: "contact2HomeValueRangeEnd", header: "Contact2 Home Value Range End" },
  { key: "contact2NetWorth", header: "Contact2 NetWorth" },
  { key: "contact2Occupation", header: "Contact2 Occupation" },
  { key: "contact2BusinessOwner", header: "Contact2 Business Owner" },

  // Contact 3
  { key: "contact3FirstName", header: "Contact3 First Name" },
  { key: "contact3MiddleName", header: "Contact3 Middle Name" },
  { key: "contact3LastName", header: "Contact3 Last Name" },
  { key: "contact3FullName", header: "Contact3 Full Name" },
  { key: "contact3PhoneNumber", header: "Contact3 Phone Number" },
  { key: "contact3PhoneType", header: "Contact3 Phone Type" },
  { key: "contact3DoNotCall", header: "Contact3 Do Not Call" },
  { key: "contact3Email", header: "Contact3 Email Address" },
  { key: "contact3Age", header: "Contact3 Age" },
  { key: "contact3Gender", header: "Contact3 Gender" },
  { key: "contact3Income", header: "Contact3 Income" },
  { key: "contact3MaritalStatus", header: "Contact3 Marital Status" },
  { key: "contact3NumberOfChildren", header: "Contact3 Number Of Children" },
  { key: "contact3Education", header: "Contact3 Education" },
  {
    key: "contact3HomeValueRangeStart",
    header: "Contact3 Home Value Range Start",
  },
  { key: "contact3HomeValueRangeEnd", header: "Contact3 Home Value Range End" },
  { key: "contact3NetWorth", header: "Contact3 NetWorth" },
  { key: "contact3Occupation", header: "Contact3 Occupation" },
  { key: "contact3BusinessOwner", header: "Contact3 Business Owner" },

  // Contact Address
  { key: "contactHouseNumber", header: "Contact House Number" },
  { key: "contactStreetName", header: "Contact Street Name" },
  { key: "contactStreetPredirection", header: "Contact Street Predirection" },
  { key: "contactStreetPostdirection", header: "Contact Street Postdirection" },
  { key: "contactStreetSuffix", header: "Contact Street Suffix" },
  { key: "contactUnit", header: "Contact Unit" },
  { key: "contactLegalUnit", header: "Contact Legal Unit" },
  { key: "contactCity", header: "Contact City" },
  { key: "contactState", header: "Contact State" },
  { key: "contactPostalCode", header: "Contact Postal Code" },
  { key: "contactPostalCodePlus4", header: "Contact Postal Code +4" },
  { key: "contactLatitude", header: "Contact Latitude" },
  { key: "contactLongitude", header: "Contact Longitude" },
  { key: "contactAccuracy", header: "Contact Accuracy" },

  // Lead Info
  { key: "leadType", header: "Lead Type" },
  { key: "hidden", header: "Hidden" },
  { key: "createTimestamp", header: "Created" },
  { key: "favorite", header: "Favorite" },
  { key: "tags", header: "Tags" },
  { key: "notes", header: "Notes" },
];
